// __EQUIP_ASSIGN__ (2026-10-04) Equipment Assignment - who runs which truck / trailer.
//
// Henry: "Make a nice convenient tab just for driver-to-equipment combination assignment, as for
// some I will be editing the setup frequently. Make a nice drag and drop feature that's visually
// pleasing where each equipment is depicted as an image representing its general shape and
// appearance. We drag and drop units (compatible ones, so no truck to truck or trailer to trailer
// or gooseneck to straight truck) together under a driver's name." / "Isaac and Samuel share the
// same fleet. Isaac is part time so shares with others."
//
// Left: the unit pool (power units + trailers, filterable, status chips, specs on hover, who is
// on each). Right: one lane per driver - his PRIMARY setup (power unit + the trailer coupled to
// it) and the units he SHARES / uses as a spare, plus what that lets him carry.
//   drag a power unit from the pool onto a lane     -> primary (replaces) or shared
//   drag a trailer onto a power unit                -> couple (hitch) - pairing rules enforced
//   drag a unit back to the pool                    -> unassign / unhitch
//   drag a power unit from one lane to another      -> moves it (from the pool = adds; sharing)
// Tap / keyboard: click (or Enter) a unit to pick it up, then click a target; Esc drops it.
// Every change saves at once (optimistic) through equipment_assign() - office only, audited -
// with Undo. The equipment check (board + hub) reads the same rows.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable,
  pointerWithin, rectIntersection,
} from '@dnd-kit/core';
import { fetchFleet } from '../services/equipmentService';
import { runAssignOps, friendlyError } from '../services/equipmentAssignService';
import {
  dropCheck, undoFor, applyOps, laneOf, couplingOf, pulledBy, driversOnTruck, truckName, trailerName, unitDown,
} from '../utils/equipmentAssign';
import { resolveUnit, unitCapacity, typeInfo, EQUIPMENT_FEATURES, fmtLb, fmtFt, todayISO, halfLoadSpec, couplingCheck } from '../utils/equipmentCompat';
import { getDefaultUnitSystem } from '../utils/units';
import { TruckArt, TrailerArt, hitchOverlap } from './equipment/EquipmentArt';
import { DowntimePanel } from './equipment/UnitDowntime';   // __UNIT_DOWNTIME__

const GREEN = 'var(--color-success)';
const RED = 'var(--color-danger)';
const AVATAR = ['#1f6feb', '#8957e5', '#bf4b8a', '#2da44e', '#bc4c00', '#0969da', '#9a6700', '#cf222e'];
const hue = (s) => AVATAR[[...String(s || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR.length];
const initials = (n) => String(n || '?').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

// collisions: the innermost target under the pointer (a power unit inside a driver's lane wins
// over the lane)
const area = (r) => (r ? r.width * r.height : Infinity);
const collide = (args) => {
  const hits = pointerWithin(args);
  if (hits.length) return [...hits].sort((a, b) => area(args.droppableRects.get(a.id)) - area(args.droppableRects.get(b.id)));
  return rectIntersection(args);
};

// a drop on the pool area (or on a card in it) of something taken from a lane returns it
function checkFor(S, item, target) {
  if (!item || !target) return null;
  let c = dropCheck(S, item, target);
  const fromLane = item.from && item.from.type !== 'pool';
  if (!c.ok && target.inPool && fromLane) c = dropCheck(S, item, { type: 'pool' });
  if (!c.ok && target.type === 'truck' && target.slot && item.kind === 'truck') c = dropCheck(S, item, { type: 'slot', driverId: target.driverId, slot: target.slot });
  return c;
}

function statusChip(row) {
  const d = unitDown(row);
  const txt = d ? (d === 'in maintenance' ? 'maintenance' : d) : 'available';
  const col = !d ? GREEN : d === 'retired' ? 'var(--text-muted)' : d === 'in maintenance' ? 'var(--color-warning)' : RED;
  return <span style={{ fontSize: '0.66rem', padding: '0 0.4rem', borderRadius: '8px', border: '1px solid ' + col, color: col, whiteSpace: 'nowrap' }}>{txt}</span>;
}
function specsOf(kind, row, units) {
  if (!row) return '';
  const cap = kind === 'truck' ? unitCapacity({ truck: row, trailer: null, combo: null }) : unitCapacity({ truck: { truck_type: 'day_cab' }, trailer: row, combo: null });
  const bits = [typeInfo(kind, kind === 'truck' ? row.truck_type : row.trailer_type).label];
  if ([row.make, row.model, row.year].some(Boolean)) bits.push([row.year, row.make, row.model].filter(Boolean).join(' '));
  if (cap && cap.deckLengthFt != null) bits.push('deck ' + fmtFt(cap.deckLengthFt, units) + ' x ' + fmtFt(cap.deckWidthFt, units)
    + (cap.insideHeightFt != null ? ' x ' + fmtFt(cap.insideHeightFt, units) + ' inside' : ''));
  if (cap && cap.payloadLb != null && !(kind === 'truck' && cap.tractorOnly)) bits.push('payload ' + fmtLb(cap.payloadLb, units));
  const feats = (row.features || []).map((f) => (EQUIPMENT_FEATURES[f] || f).replace(/ \(.*\)$/, ''));
  if (feats.length) bits.push(feats.join(', '));
  const hl = halfLoadText(row, units);
  if (hl) bits.push(hl);
  if (unitDown(row)) bits.push(unitDown(row).toUpperCase());
  return bits.join('\n');
}
// __EQUIP_R5__ the unit's own half-load caps (gross), or '' (fleet-wide rule / none)
function halfLoadText(row, units) {
  const h = halfLoadSpec(row);
  if (!h) return '';
  const parts = [];
  if (h.totalLb !== null) parts.push('total ' + fmtLb(h.totalLb, units));
  if (h.axles.length) parts.push(h.axles.map((a) => a.axle + ' ' + fmtLb(a.lb, units)).join(' + '));
  return 'Half-load: ' + fmtLb(h.grossLb, units) + ' gross (' + parts.join('; ') + ')'
    + (h.tareLb !== null ? ', cargo up to ' + fmtLb(Math.max(0, h.grossLb - h.tareLb), units) : '');
}
const hlChip = (row, units) => {
  const h = halfLoadSpec(row);
  if (!h) return null;
  return <span title={halfLoadText(row, units)} style={{ fontSize: '0.64rem', padding: '0 0.35rem', borderRadius: '8px', border: '1px solid var(--color-info, #58a6ff)', color: 'var(--color-info, #58a6ff)', whiteSpace: 'nowrap' }}>HL {fmtLb(h.grossLb, units)}</span>;
};
const shortName = (kind, row) => {
  if (!row) return '';
  const mm = [row.make, row.model].filter(Boolean).join(' ');
  return mm || typeInfo(kind, kind === 'truck' ? row.truck_type : row.trailer_type).label;
};

function Avatars({ list }) {
  if (!list || !list.length) return null;
  return (
    <span style={{ display: 'inline-flex', marginLeft: '0.2rem' }}>
      {list.slice(0, 5).map((x, i) => (
        <span key={x.driver.id} title={x.driver.name + (x.assignment.is_primary ? ' (primary)' : ' (shared)')}
          style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: hue(x.driver.name), color: '#fff', fontSize: '0.6rem', fontWeight: 'bold',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: i ? '-6px' : 0,
            border: x.assignment.is_primary ? '2px solid #e6edf3' : '2px solid var(--surface-1)' }}>{initials(x.driver.name)}</span>
      ))}
      {list.length > 5 && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: '0.2rem' }}>+{list.length - 5}</span>}
    </span>
  );
}

// ---------------------------------------------------------------- drag / drop building blocks
function DropZone({ id, target, S, active, selected, onTap, children, style, className, empty, hint = true }) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { target } });
  const item = active || selected;
  const chk = item ? checkFor(S, item, target) : null;
  const ok = !!(chk && chk.ok);
  const bad = !!(chk && !chk.ok && !chk.noop && chk.reason);
  const hot = isOver && !!active;
  const border = hot ? (ok ? '2px solid ' + GREEN : bad ? '2px solid ' + RED : '2px solid var(--border-default)')
    : (item && ok && hint ? '2px dashed rgba(63,185,80,0.55)' : (style && style.border) || '2px solid transparent');
  const tap = (e) => { if (!selected) return; e.stopPropagation(); onTap(target); };
  return (
    <div ref={setNodeRef} className={className} onClick={tap}
      role={selected ? 'button' : undefined} tabIndex={selected ? 0 : undefined}
      onKeyDown={(e) => { if (selected && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); onTap(target); } }}
      title={item && chk && !chk.ok && chk.reason ? chk.reason : undefined}
      style={{ position: 'relative', borderRadius: '8px', transition: 'border-color 120ms, background-color 120ms', ...style, border,
        backgroundColor: hot ? (ok ? 'rgba(46,160,67,0.12)' : bad ? 'rgba(248,81,73,0.12)' : (style && style.backgroundColor)) : (style && style.backgroundColor),
        cursor: selected ? (ok ? 'copy' : 'not-allowed') : (style && style.cursor) }}>
      {children}
      {empty}
      {hot && chk && (chk.ok || bad) && (
        <div style={{ position: 'absolute', left: '50%', top: '-0.9rem', transform: 'translateX(-50%)', zIndex: 20, width: 'max-content', maxWidth: '300px', whiteSpace: 'normal',
          backgroundColor: ok ? GREEN : RED, color: '#fff', fontSize: '0.72rem', padding: '0.2rem 0.55rem', borderRadius: '10px', boxShadow: 'var(--shadow-md)', pointerEvents: 'none', textAlign: 'center' }}>
          {ok ? chk.summary + (chk.warn ? ' - ' + chk.warn : '') : chk.reason}
        </div>
      )}
    </div>
  );
}

function Draggable({ id, item, children, selected, onSelect, style, title }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: { item } });
  const picked = selected && selected.kind === item.kind && selected.id === item.id && JSON.stringify(selected.from) === JSON.stringify(item.from);
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} title={title}
      onClick={(e) => { e.stopPropagation(); onSelect(item); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onSelect(item); } }}
      role="button" tabIndex={0} aria-pressed={picked} aria-label={(item.kind === 'truck' ? 'Power unit ' : 'Trailer ') + (item.label || '')}
      style={{ cursor: 'grab', opacity: isDragging ? 0.35 : 1, transition: 'opacity 120ms, transform 120ms, box-shadow 120ms', touchAction: 'none',
        outline: picked ? '2px solid var(--accent-blue)' : 'none', outlineOffset: '2px', borderRadius: '8px', ...style }}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- the view
export default function EquipmentAssignmentView() {
  const units = getDefaultUnitSystem();
  const [S, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [show, setShow] = useState('all');
  const [showRetired, setShowRetired] = useState(false);
  const [showOff, setShowOff] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const today = todayISO();

  const load = useCallback(async () => {
    try {
      const f = await fetchFleet(true);
      if (f.error) setErr('Could not read the fleet: ' + f.error);
      else { setErr(null); setS(f); }
    } catch (e) { setErr(String(e.message || e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { setSelected(null); setEditing(null); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);
  useEffect(() => {
    if (!toast || toast.sticky) return undefined;
    const t = setTimeout(() => setToast(null), toast.error ? 7000 : 9000);
    return () => clearTimeout(t);
  }, [toast]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const lastDrag = useRef(0);   // the click that ends a drag must not pick the unit up

  const save = async (ops, summary, warn, undo, isUndo) => {
    const before = S;
    setS(applyOps(S, ops));
    setBusy(true);
    try {
      await runAssignOps(ops);
      setToast({ text: isUndo ? 'Undone.' : summary, warn: isUndo ? null : warn, undo: isUndo ? null : undo });
      load();
    } catch (e) {
      setS(before);
      setToast({ error: true, text: 'Not saved: ' + friendlyError(e) });
      load();
    } finally { setBusy(false); }
  };

  const perform = (item, target) => {
    if (!S || !item || !target) return;
    const res = checkFor(S, item, target);
    setSelected(null);
    if (!res || !res.ok) {
      if (res && !res.noop && res.reason) setToast({ error: true, text: res.reason });
      return;
    }
    save(res.ops, res.summary, res.warn, undoFor(S, res.ops));
  };
  const onSelect = (item) => {
    if (Date.now() - lastDrag.current < 300) return;
    if (selected && !(selected.kind === item.kind && selected.id === item.id && JSON.stringify(selected.from) === JSON.stringify(item.from))) {
      // a picked-up trailer clicked onto a power unit card = hitch it there
      if (selected.kind === 'trailer' && item.kind === 'truck') {
        perform(selected, item.from.type === 'lane' ? { type: 'truck', truckId: item.id, driverId: item.from.driverId, slot: item.from.slot } : { type: 'truck', truckId: item.id, inPool: true });
        return;
      }
    }
    setSelected((cur) => (cur && cur.kind === item.kind && cur.id === item.id && JSON.stringify(cur.from) === JSON.stringify(item.from) ? null : item));
  };

  const drivers = useMemo(() => (S ? [...S.drivers].filter((d) => showOff || d.is_available !== false)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))) : []), [S, showOff]);

  if (err && !S) return <div style={{ padding: '1rem', color: RED }}>{err}</div>;
  if (!S) return <div style={{ padding: '1rem', color: 'var(--text-muted)' }}>Loading the fleet...</div>;

  const match = (row, k) => {
    const name = k === 'truck' ? row.truck_number : row.trailer_number;
    const txt = [name, row.make, row.model, typeInfo(k, k === 'truck' ? row.truck_type : row.trailer_type).label].join(' ').toLowerCase();
    if (q && !txt.includes(q.toLowerCase())) return false;
    if (!showRetired && row.is_active === false) return false;
    if (show === 'shop' && !(unitDown(row) === 'in maintenance' || unitDown(row) === 'out of service')) return false;
    if (show === 'free') {
      if (k === 'truck' && driversOnTruck(S, row.id).length) return false;
      if (k === 'trailer' && pulledBy(S, row.id)) return false;
    }
    return true;
  };
  const trucks = [...S.trucks].filter((t) => kind !== 'trailer' && match(t, 'truck')).sort((a, b) => truckName(a).localeCompare(truckName(b)));
  const trailers = [...S.trailers].filter((t) => kind !== 'truck' && match(t, 'trailer')).sort((a, b) => trailerName(a).localeCompare(trailerName(b)));
  const truckById = (id) => S.trucks.find((t) => t.id === id) || null;
  const trailerById = (id) => S.trailers.find((t) => t.id === id) || null;
  const zoneProps = { S, active, selected, onTap: (target) => perform(selected, target) };

  // ---------- pool cards
  const poolTruck = (t) => {
    const item = { kind: 'truck', id: t.id, from: { type: 'pool' }, label: truckName(t) };
    const on = driversOnTruck(S, t.id);
    const tl = trailerById(couplingOf(S, t.id));
    return (
      <DropZone key={t.id} id={'drop:ptruck:' + t.id} target={{ type: 'truck', truckId: t.id, inPool: true }} {...zoneProps}
        hint={!!(active || selected) && (active || selected).kind === 'trailer' && (active || selected).from.type === 'pool'}
        style={{ backgroundColor: 'var(--surface-2)', border: '2px solid var(--border-default)' }}>
        <Draggable id={'pool:truck:' + t.id} item={item} selected={selected} onSelect={onSelect} title={specsOf('truck', t, units)} style={{ padding: '0.4rem 0.5rem' }}>
          <TruckArt unit={t} width={128} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.2rem' }}>
            <strong style={{ fontSize: '0.86rem' }}>{truckName(t)}</strong>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{shortName('truck', t)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.15rem', minHeight: '22px' }}>
            {statusChip(t)}
            {hlChip(t, units)}
            {tl && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>pulls {trailerName(tl)}</span>}
            <span style={{ flex: 1 }} />
            <Avatars list={on} />
          </div>
        </Draggable>
      </DropZone>
    );
  };
  const poolTrailer = (t) => {
    const item = { kind: 'trailer', id: t.id, from: { type: 'pool' }, label: trailerName(t) };
    const by = truckById(pulledBy(S, t.id));
    return (
      <div key={t.id} style={{ backgroundColor: 'var(--surface-2)', border: '2px solid var(--border-default)', borderRadius: '8px' }}>
        <Draggable id={'pool:trailer:' + t.id} item={item} selected={selected} onSelect={onSelect} title={specsOf('trailer', t, units)} style={{ padding: '0.4rem 0.5rem' }}>
          <TrailerArt unit={t} width={168} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.2rem' }}>
            <strong style={{ fontSize: '0.86rem' }}>{trailerName(t)}</strong>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{shortName('trailer', t)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.15rem', minHeight: '22px' }}>
            {statusChip(t)}
            {hlChip(t, units)}
            {by ? <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>on {truckName(by)}</span> : <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>unhitched</span>}
            <span style={{ flex: 1 }} />
            {by && <Avatars list={driversOnTruck(S, by.id)} />}
          </div>
        </Draggable>
      </div>
    );
  };

  // ---------- a power unit (+ its trailer) inside a lane
  const laneUnit = (driver, row, slot, small) => {
    const t = row.truck; const tl = row.trailer;
    const a = row.assignment;
    const scale = small ? 0.62 : 0.9;
    const overlap = hitchOverlap(t, tl) * scale;
    const others = driversOnTruck(S, t.id).filter((x) => x.driver.id !== driver.id);
    const isEditing = editing && editing.driverId === driver.id && editing.truckId === t.id;
    return (
      <div key={t.id} style={{ display: 'inline-block', verticalAlign: 'top', marginRight: '0.5rem', marginBottom: '0.3rem' }}>
        <DropZone id={'drop:truck:' + driver.id + ':' + t.id} target={{ type: 'truck', truckId: t.id, driverId: driver.id, slot }} {...zoneProps}
          style={{ padding: '0.3rem 0.4rem', backgroundColor: 'var(--surface-1)', border: '2px solid var(--border-default)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            {tl && (
              <Draggable id={'lane:' + driver.id + ':trailer:' + tl.id} item={{ kind: 'trailer', id: tl.id, from: { type: 'coupled', truckId: t.id, driverId: driver.id }, label: trailerName(tl) }}
                selected={selected} onSelect={onSelect} title={specsOf('trailer', tl, units)} style={{ marginRight: -overlap + 'px', position: 'relative', zIndex: 1 }}>
                <TrailerArt unit={tl} width={170 * scale} />
              </Draggable>
            )}
            <Draggable id={'lane:' + driver.id + ':truck:' + t.id} item={{ kind: 'truck', id: t.id, from: { type: 'lane', driverId: driver.id, slot }, label: truckName(t) }}
              selected={selected} onSelect={onSelect} title={specsOf('truck', t, units)} style={{ position: 'relative', zIndex: 0 }}>
              <TruckArt unit={t} width={130 * scale} />
            </Draggable>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.15rem', fontSize: small ? '0.74rem' : '0.8rem' }}>
            <strong>{truckName(t)}{tl ? ' + ' + trailerName(tl) : ''}</strong>
            {statusChip(t)}
            {tl && unitDown(tl) && statusChip(tl)}
            {hlChip(t, units)}{tl && hlChip(tl, units)}
            {tl && (() => {   // __EQUIP_R6__ an existing pair whose hitches do not match (entered elsewhere)
              const cc = couplingCheck({ ...t, status: 'available', is_active: true }, { ...tl, status: 'available', is_active: true });
              return cc.ok ? null : <span data-hitch-mismatch title={cc.reason} style={{ fontSize: '0.64rem', padding: '0 0.35rem', borderRadius: '8px', border: '1px solid ' + RED, color: RED, whiteSpace: 'nowrap' }}>hitch mismatch</span>;
            })()}
            {(a.start_date || a.end_date) && <span style={{ fontSize: '0.68rem', color: 'var(--color-info, var(--accent-blue))' }}>{(a.start_date || '...') + ' to ' + (a.end_date || '...')}</span>}
            {others.length > 0 && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>shared with</span>}
            <Avatars list={others} />
            <span style={{ flex: 1 }} />
            {slot === 'shared' && <button type="button" title="Make this his primary setup" onClick={(e) => { e.stopPropagation(); perform({ kind: 'truck', id: t.id, from: { type: 'lane', driverId: driver.id, slot } }, { type: 'slot', driverId: driver.id, slot: 'primary' }); }} style={iconBtn}>&#9733;</button>}
            <button type="button" title="Dates / note" onClick={(e) => { e.stopPropagation(); setEditing(isEditing ? null : { driverId: driver.id, truckId: t.id, start: a.start_date || '', end: a.end_date || '', note: a.note || '' }); }} style={iconBtn}>&#128197;</button>
            <button type="button" title={'Take ' + truckName(t) + ' off ' + driver.name} onClick={(e) => { e.stopPropagation(); perform({ kind: 'truck', id: t.id, from: { type: 'lane', driverId: driver.id, slot } }, { type: 'pool' }); }} style={iconBtn}>&times;</button>
          </div>
        </DropZone>
        {isEditing && (
          <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '0.3rem', padding: '0.45rem', backgroundColor: 'var(--surface-3)', borderRadius: '6px', fontSize: '0.75rem', display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label>From<br /><input type="date" value={editing.start} onChange={(e) => setEditing({ ...editing, start: e.target.value })} style={inp} /></label>
            <label>To<br /><input type="date" value={editing.end} onChange={(e) => setEditing({ ...editing, end: e.target.value })} style={inp} /></label>
            <label style={{ flex: 1, minWidth: '120px' }}>Note<br /><input value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder="e.g. part time - Tue / Thu" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} /></label>
            <button type="button" style={{ ...iconBtn, padding: '0.3rem 0.6rem', backgroundColor: 'var(--accent-primary)' }} disabled={busy || (editing.start && editing.end && editing.end < editing.start)}
              onClick={() => {
                const op = { op: 'assign', driver_id: driver.id, truck_id: t.id, start_date: editing.start || null, end_date: editing.end || null, note: editing.note || null };
                setEditing(null);
                save([op], truckName(t) + ' for ' + driver.name + ': ' + (editing.start || editing.end ? (editing.start || '...') + ' to ' + (editing.end || '...') : 'any day'), null, undoFor(S, [op]));
              }}>Save</button>
          </div>
        )}
      </div>
    );
  };

  const lane = (d) => {
    const L = laneOf(S, d.id);
    const unit = resolveUnit({ driverId: d.id, fleet: S, day: today });
    const cap = unit && !unit.missing ? unitCapacity(unit, null, today) : null;
    const capText = !unit ? 'No unit today - every job for him shows a "no unit" warning.'
      : [unit.source === 'spare_truck' && unit.ownUnit && unit.ownUnit.truck ? 'Today: ' + truckName(unit.truck) + ' (spare - ' + truckName(unit.ownUnit.truck) + ' is ' + (unitDown(unit.ownUnit.truck) || unitDown(unit.ownUnit.trailer) || 'down') + ')' : null,
        unit.choices ? 'shares ' + unit.choices.length + ' units - the job check asks which one' : null,
        cap && cap.payloadLb != null ? 'payload ' + fmtLb(cap.payloadLb, units) : (cap && cap.tractorOnly ? 'tractor only' : 'payload unknown'),
        cap && cap.deckLengthFt != null ? 'deck ' + fmtFt(cap.deckLengthFt, units) + ' x ' + fmtFt(cap.deckWidthFt, units) : null,
        cap && cap.features.length ? cap.features.filter((f) => ['ramps', 'crane', 'self_unload', 'tilt', 'winch', 'liftgate', 'dock_height', 'tarps', 'beavertail'].includes(f)).map((f) => (EQUIPMENT_FEATURES[f] || f).replace(/ \(.*\)$/, '').toLowerCase()).join(', ') : null,
      ].filter(Boolean).join(' · ');
    return (
      <div key={d.id} data-lane={d.name} style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-default)', borderRadius: '10px', padding: '0.6rem 0.7rem', marginBottom: '0.7rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.45rem' }}>
          <span style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: hue(d.name), color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem', flexShrink: 0 }}>{initials(d.name)}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 'bold' }}>{d.name || 'Driver'}{d.is_available === false && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: 'var(--text-muted)' }}>(unavailable)</span>}</div>
            <div style={{ fontSize: '0.74rem', color: unit ? 'var(--text-secondary)' : 'var(--color-warning)' }}>{capText}</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(230px, 1.1fr) minmax(190px, 1fr)', gap: '0.6rem' }}>
          <DropZone id={'drop:slot:' + d.id + ':primary'} target={{ type: 'slot', driverId: d.id, slot: 'primary' }} {...zoneProps}
            style={{ padding: '0.35rem', minHeight: '84px', backgroundColor: 'rgba(13,17,23,0.45)', border: '2px solid transparent' }}>
            <div style={slotLabel}>Primary setup</div>
            {L.primary ? laneUnit(d, L.primary, 'primary', false)
              : <div style={emptyTxt}>Drop a power unit here (then a trailer on it)</div>}
          </DropZone>
          <DropZone id={'drop:slot:' + d.id + ':shared'} target={{ type: 'slot', driverId: d.id, slot: 'shared' }} {...zoneProps}
            style={{ padding: '0.35rem', minHeight: '84px', backgroundColor: 'rgba(13,17,23,0.3)', border: '2px solid transparent' }}>
            <div style={slotLabel}>Also drives - shared / spare</div>
            {L.shared.map((row) => laneUnit(d, row, 'shared', true))}
            {!L.shared.length && <div style={emptyTxt}>Drop units he shares with others or takes as a spare</div>}
          </DropZone>
        </div>
      </div>
    );
  };

  const activeArt = active ? (active.kind === 'truck' ? <TruckArt unit={truckById(active.id)} width={130} /> : <TrailerArt unit={trailerById(active.id)} width={170} />) : null;

  return (
    <DndContext sensors={sensors} collisionDetection={collide}
      onDragStart={(e) => { setSelected(null); setEditing(null); setActive(e.active.data.current.item); }}
      onDragCancel={() => { lastDrag.current = Date.now(); setActive(null); }}
      onDragEnd={(e) => { lastDrag.current = Date.now(); const it = active; setActive(null); if (e.over && e.over.data.current) perform(it, e.over.data.current.target); }}>
      <div data-equipment-assignment style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 400px) 1fr', gap: '1rem', alignItems: 'start' }}>
        {/* ---------------- pool */}
        <DropZone id="drop:pool" target={{ type: 'pool', inPool: true }} {...zoneProps} hint={false}
          style={{ backgroundColor: 'var(--surface-1)', border: '2px solid var(--border-default)', padding: '0.7rem', position: 'sticky', top: '0.5rem', maxHeight: 'calc(100vh - 2rem)', overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h3 style={{ margin: '0 0 0.4rem', color: 'var(--text-primary)', fontSize: '1rem' }}>Units</h3>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>drag onto a driver - drop back here to take off</span>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search t04, Kenworth, gooseneck..." aria-label="Search units"
            style={{ ...inp, width: '100%', boxSizing: 'border-box', marginBottom: '0.4rem' }} />
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {[['all', 'All'], ['truck', 'Power units'], ['trailer', 'Trailers']].map(([k, l]) => <button key={k} type="button" onClick={() => setKind(k)} style={chip(kind === k)}>{l}</button>)}
            <span style={{ width: '0.4rem' }} />
            {[['all', 'Any status'], ['free', 'Free'], ['shop', 'In the shop']].map(([k, l]) => <button key={k} type="button" onClick={() => setShow(k)} style={chip(show === k)}>{l}</button>)}
            <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
              <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />retired</label>
          </div>
          {kind !== 'trailer' && <div style={groupLabel}>Power units ({trucks.length})</div>}
          {kind !== 'trailer' && <div style={grid}>{trucks.map(poolTruck)}</div>}
          {kind !== 'truck' && <div style={{ ...groupLabel, marginTop: '0.7rem' }}>Trailers ({trailers.length})</div>}
          {kind !== 'truck' && <div style={grid}>{trailers.map(poolTrailer)}</div>}
        </DropZone>

        {/* ---------------- drivers */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>Drivers</h3>
            <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
              Primary = his usual setup (the job check uses it). Shared / spare = units he can also take; a unit can sit on several drivers.
            </span>
            <span style={{ flex: 1 }} />
            <label style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />show unavailable drivers</label>
            {busy && <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>saving...</span>}
          </div>
          {selected && (
            <div role="status" style={{ marginBottom: '0.5rem', padding: '0.45rem 0.7rem', borderRadius: '8px', backgroundColor: 'rgba(56,139,253,0.15)', border: '1px solid var(--accent-blue)', fontSize: '0.8rem' }}>
              <strong>{selected.label}</strong> picked up - click a driver's <em>Primary</em> or <em>Shared</em> area{selected.kind === 'trailer' ? ', or a power unit to hitch it' : ''}, or the Units list to take it off. <span style={{ color: 'var(--text-muted)' }}>Esc to cancel.</span>
            </div>
          )}
          {err && <div style={{ color: RED, fontSize: '0.8rem', marginBottom: '0.4rem' }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))', gap: '0 0.7rem', alignItems: 'start' }}>{drivers.map(lane)}</div>
          {/* __UNIT_DOWNTIME__ (2026-10-08) dated maintenance / out-of-service days for any unit */}
          {S && <DowntimePanel trucks={S.trucks} trailers={S.trailers} />}
        </div>
      </div>
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }}>
        {activeArt && <div style={{ filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.6))', transform: 'rotate(-2deg) scale(1.04)', cursor: 'grabbing' }}>{activeArt}</div>}
      </DragOverlay>
      {toast && (
        <div role="status" aria-live="polite" style={{ position: 'fixed', right: '1.2rem', bottom: '1.2rem', zIndex: 4000, maxWidth: '440px', padding: '0.65rem 0.8rem', borderRadius: '10px',
          backgroundColor: toast.error ? 'rgba(110,22,22,0.96)' : 'var(--surface-3)', border: '1px solid ' + (toast.error ? RED : 'var(--border-default)'), boxShadow: 'var(--shadow-lg)', fontSize: '0.84rem', display: 'flex', gap: '0.7rem', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div>{toast.text}</div>
            {toast.warn && <div style={{ color: 'var(--color-warning)', fontSize: '0.76rem', marginTop: '0.15rem' }}>{toast.warn} - assigned anyway; the job check will say so (and use a spare he shares, if any).</div>}
          </div>
          {toast.undo && <button type="button" disabled={busy} onClick={() => { const u = toast.undo; setToast(null); save([u], '', null, null, true); }}
            style={{ ...iconBtn, padding: '0.3rem 0.7rem', fontWeight: 'bold', color: 'var(--accent-blue)' }}>Undo</button>}
          <button type="button" aria-label="Close" onClick={() => setToast(null)} style={iconBtn}>&times;</button>
        </div>
      )}
    </DndContext>
  );
}

const inp = { padding: '0.35rem 0.45rem', backgroundColor: 'var(--surface-input, var(--bg-primary))', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px', fontSize: '0.8rem' };
const iconBtn = { background: 'transparent', border: '1px solid var(--border-default)', color: 'var(--text-secondary)', borderRadius: '5px', padding: '0 0.35rem', cursor: 'pointer', fontSize: '0.78rem', lineHeight: '1.4' };
const chip = (on) => ({ padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.72rem', cursor: 'pointer', border: '1px solid ' + (on ? 'var(--accent-blue)' : 'var(--border-default)'), backgroundColor: on ? 'rgba(56,139,253,0.18)' : 'transparent', color: 'var(--text-primary)' });
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '0.45rem' };
const groupLabel = { fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', margin: '0.2rem 0 0.35rem' };
const slotLabel = { fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.25rem' };
const emptyTxt = { fontSize: '0.76rem', color: 'var(--text-muted)', padding: '0.9rem 0.4rem', textAlign: 'center', border: '1px dashed var(--border-default)', borderRadius: '6px' };
